/**
 * DecisionsTab - Project decisions display and management
 *
 * Displays decisions made during the project pipeline, grouped by pipeline step.
 * Users can view decision details, override values, reset overrides, create
 * new decisions, soft-delete/restore decisions, and trigger reprocessing.
 */
import React, { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  Button,
  Text,
  Badge,
  LoadingSpinner,
  Tooltip,
  tokens,
} from '../../design-system';
import apiClient from '../../services/unifiedApiClient';
import TextDiff from '../TextDiff';

// Pipeline step ordering and labels
const PIPELINE_STEPS = [
  { key: 'idea_spec', label: 'Idea Spec' },
  { key: 'feature_analysis', label: 'Feature Analysis' },
  { key: 'story_analysis', label: 'Story Analysis' },
  { key: 'tech_stack', label: 'Tech Stack' },
  { key: 'event_model', label: 'Event Model' },
  { key: 'requirements', label: 'Requirements' },
  { key: 'screen_generation', label: 'Screen Generation' },
];

// Category configuration
const CATEGORY_CONFIG = {
  architecture: { label: 'Architecture', color: tokens.colors.primary[500] },
  scope: { label: 'Scope', color: tokens.colors.info[500] },
  technology: { label: 'Technology', color: tokens.colors.success[500] },
  priority: { label: 'Priority', color: tokens.colors.warning[500] },
  data_model: { label: 'Data Model', color: tokens.colors.primary[400] },
  integration: { label: 'Integration', color: tokens.colors.info[400] },
  security: { label: 'Security', color: tokens.colors.error[500] },
  ux: { label: 'UX', color: tokens.colors.success[400] },
};

const CATEGORY_OPTIONS = Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => ({
  value: key,
  label: cfg.label,
}));

// Helper to get the confidence indicator color
const getConfidenceColor = (confidence) => {
  if (confidence < 0.5) return tokens.colors.error[500];
  if (confidence < 0.8) return tokens.colors.warning[500];
  return tokens.colors.success[500];
};

// Helper to get pipeline step label
const getStepLabel = (stepKey) => {
  const step = PIPELINE_STEPS.find((s) => s.key === stepKey);
  return step ? step.label : stepKey;
};

// Helper to slugify a title into a decision_key
const slugify = (text) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 100);

const DecisionsTab = ({ task }) => {
  const queryClient = useQueryClient();
  const [collapsedSteps, setCollapsedSteps] = useState({});
  const [expandedReasoning, setExpandedReasoning] = useState({});
  const [expandedAlternatives, setExpandedAlternatives] = useState({});
  const [editingDecisionId, setEditingDecisionId] = useState(null);
  const [editChosenValue, setEditChosenValue] = useState('');
  const [editReasoning, setEditReasoning] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);

  // Cascade preview state
  const [previewData, setPreviewData] = useState(null); // DecisionPreviewResponse
  const [selectedCascades, setSelectedCascades] = useState({}); // { index: bool }
  const [previewDecisionId, setPreviewDecisionId] = useState(null);

  // Add Decision form state
  const [newDecision, setNewDecision] = useState({
    pipeline_step: 'event_model',
    title: '',
    description: '',
    category: 'scope',
    chosen_value: '',
    reasoning: '',
  });

  const planId = task?.task_id;

  // Fetch decisions
  const { data, isLoading, isError } = useQuery({
    queryKey: ['plans', planId, 'decisions'],
    queryFn: async () => {
      const response = await apiClient.get(`/plans/${planId}/decisions`);
      return response?.data || response;
    },
    enabled: !!planId,
  });

  const decisions = data?.decisions || [];
  const totalCount = data?.total || 0;
  const userOverrides = data?.user_overrides || 0;
  const pendingDeletes = data?.pending_deletes || 0;
  const byStep = data?.by_step || {};

  // Preview mutation — analyze cascade effects before applying
  const previewMutation = useMutation({
    mutationFn: async ({ decisionId, chosenValue, reasoning }) => {
      const response = await apiClient.previewDecisionCascade(planId, decisionId, {
        chosen_value: chosenValue,
        reasoning: reasoning || undefined,
      });
      return response?.data || response;
    },
    onSuccess: (data, variables) => {
      setPreviewData(data);
      setPreviewDecisionId(variables.decisionId);
      // Select all cascades by default
      const initial = {};
      (data.cascade_changes || []).forEach((_, i) => { initial[i] = true; });
      setSelectedCascades(initial);
      setEditingDecisionId(null);
    },
  });

  // Apply mutation — save override + approved cascades
  const applyMutation = useMutation({
    mutationFn: async ({ decisionId, chosenValue, reasoning, approvedCascades }) => {
      const response = await apiClient.applyDecisionCascade(planId, decisionId, {
        chosen_value: chosenValue,
        reasoning: reasoning || undefined,
        approved_cascades: approvedCascades,
      });
      return response?.data || response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['plans', planId, 'decisions']);
      setPreviewData(null);
      setPreviewDecisionId(null);
      setSelectedCascades({});
      setEditChosenValue('');
      setEditReasoning('');
    },
  });

  // Reset mutation (POST)
  const resetMutation = useMutation({
    mutationFn: async (decisionId) => {
      const response = await apiClient.post(
        `/plans/${planId}/decisions/${decisionId}/reset`
      );
      return response?.data || response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['plans', planId, 'decisions']);
    },
  });

  // Create mutation (POST)
  const createMutation = useMutation({
    mutationFn: async (body) => {
      const response = await apiClient.post(
        `/plans/${planId}/decisions`,
        body
      );
      return response?.data || response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['plans', planId, 'decisions']);
      setShowAddForm(false);
      setNewDecision({
        pipeline_step: 'event_model',
        title: '',
        description: '',
        category: 'scope',
        chosen_value: '',
        reasoning: '',
      });
    },
  });

  // Delete (soft-delete) mutation (DELETE)
  const deleteMutation = useMutation({
    mutationFn: async (decisionId) => {
      const response = await apiClient.delete(
        `/plans/${planId}/decisions/${decisionId}`
      );
      return response?.data || response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['plans', planId, 'decisions']);
    },
  });

  // Restore mutation (POST)
  const restoreMutation = useMutation({
    mutationFn: async (decisionId) => {
      const response = await apiClient.post(
        `/plans/${planId}/decisions/${decisionId}/restore`
      );
      return response?.data || response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['plans', planId, 'decisions']);
    },
  });

  // Group decisions by pipeline_step, preserving PIPELINE_STEPS order
  const groupedDecisions = useMemo(() => {
    const grouped = {};
    decisions.forEach((d) => {
      const step = d.pipeline_step || 'unknown';
      if (!grouped[step]) grouped[step] = [];
      grouped[step].push(d);
    });
    return grouped;
  }, [decisions]);

  // Ordered steps that have decisions
  const orderedSteps = useMemo(() => {
    const stepsWithDecisions = PIPELINE_STEPS.filter(
      (step) => groupedDecisions[step.key]?.length > 0
    );
    // Also include any steps not in PIPELINE_STEPS
    const knownKeys = new Set(PIPELINE_STEPS.map((s) => s.key));
    Object.keys(groupedDecisions).forEach((key) => {
      if (!knownKeys.has(key)) {
        stepsWithDecisions.push({ key, label: getStepLabel(key) });
      }
    });
    return stepsWithDecisions;
  }, [groupedDecisions]);

  // Toggle collapsed state for a step
  const toggleStep = useCallback((stepKey) => {
    setCollapsedSteps((prev) => ({ ...prev, [stepKey]: !prev[stepKey] }));
  }, []);

  // Toggle reasoning visibility
  const toggleReasoning = useCallback((decisionId) => {
    setExpandedReasoning((prev) => ({
      ...prev,
      [decisionId]: !prev[decisionId],
    }));
  }, []);

  // Toggle alternatives visibility
  const toggleAlternatives = useCallback((decisionId) => {
    setExpandedAlternatives((prev) => ({
      ...prev,
      [decisionId]: !prev[decisionId],
    }));
  }, []);

  // Start editing a decision
  const handleStartEdit = useCallback((decision) => {
    setEditingDecisionId(decision.id);
    setEditChosenValue(decision.chosen_value || '');
    setEditReasoning('');
  }, []);

  // Cancel editing
  const handleCancelEdit = useCallback(() => {
    setEditingDecisionId(null);
    setEditChosenValue('');
    setEditReasoning('');
  }, []);

  // Save override — triggers cascade preview first
  const handleSaveOverride = useCallback(() => {
    if (!editingDecisionId || !editChosenValue.trim()) return;
    previewMutation.mutate({
      decisionId: editingDecisionId,
      chosenValue: editChosenValue.trim(),
      reasoning: editReasoning.trim() || undefined,
    });
  }, [editingDecisionId, editChosenValue, editReasoning, previewMutation]);

  // Apply from cascade preview
  const handleApplyCascade = useCallback(() => {
    if (!previewData || !previewDecisionId) return;
    const approved = (previewData.cascade_changes || []).filter((_, i) => selectedCascades[i]);
    applyMutation.mutate({
      decisionId: previewDecisionId,
      chosenValue: previewData.new_value,
      reasoning: editReasoning.trim() || undefined,
      approvedCascades: approved,
    });
  }, [previewData, previewDecisionId, selectedCascades, editReasoning, applyMutation]);

  // Toggle a cascade change selection
  const toggleCascadeSelection = useCallback((index) => {
    setSelectedCascades(prev => ({ ...prev, [index]: !prev[index] }));
  }, []);

  // Dismiss cascade preview
  const handleDismissPreview = useCallback(() => {
    setPreviewData(null);
    setPreviewDecisionId(null);
    setSelectedCascades({});
  }, []);

  // Reset an override
  const handleReset = useCallback(
    (decisionId) => {
      resetMutation.mutate(decisionId);
    },
    [resetMutation]
  );

  // Submit new decision
  const handleCreateDecision = useCallback(() => {
    if (!newDecision.title.trim() || !newDecision.chosen_value.trim()) return;
    createMutation.mutate({
      pipeline_step: newDecision.pipeline_step,
      decision_key: slugify(newDecision.title),
      title: newDecision.title.trim(),
      description: newDecision.description.trim(),
      category: newDecision.category,
      chosen_value: newDecision.chosen_value.trim(),
      reasoning: newDecision.reasoning.trim(),
    });
  }, [newDecision, createMutation]);

  // Loading state
  if (isLoading) {
    return (
      <Card style={styles.tabCard}>
        <div style={styles.loadingContainer}>
          <LoadingSpinner size="md" />
          <Text style={styles.loadingText}>Loading decisions...</Text>
        </div>
      </Card>
    );
  }

  // Error state
  if (isError) {
    return (
      <Card style={styles.tabCard}>
        <Text style={styles.tabTitle}>Decisions</Text>
        <div style={styles.emptyState}>
          <Text style={styles.emptyText}>
            Failed to load decisions. Please try again.
          </Text>
        </div>
      </Card>
    );
  }

  return (
    <Card style={styles.tabCard}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <Text style={styles.tabTitle}>Decisions</Text>
          <Text style={styles.description}>
            Decisions made during project analysis. Override any decision to
            adjust the output.
          </Text>
        </div>
        <div style={styles.headerRight}>
          <div style={styles.headerStats}>
            <Badge variant="neutral" size="sm">
              {totalCount} total
            </Badge>
            {userOverrides > 0 && (
              <Badge variant="info" size="sm">
                {userOverrides} override{userOverrides !== 1 ? 's' : ''}
              </Badge>
            )}
            {pendingDeletes > 0 && (
              <Badge variant="error" size="sm">
                {pendingDeletes} pending delete{pendingDeletes !== 1 ? 's' : ''}
              </Badge>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAddForm((prev) => !prev)}
          >
            {showAddForm ? 'Cancel' : 'Add Decision'}
          </Button>
        </div>
      </div>

      {/* Add Decision Form */}
      {showAddForm && (
        <div style={styles.addForm}>
          <Text style={styles.addFormTitle}>Add Decision</Text>
          <div style={styles.addFormGrid}>
            <div style={styles.addFormField}>
              <label style={styles.editLabel}>Pipeline Step</label>
              <select
                value={newDecision.pipeline_step}
                onChange={(e) =>
                  setNewDecision((prev) => ({
                    ...prev,
                    pipeline_step: e.target.value,
                  }))
                }
                style={styles.selectInput}
              >
                {PIPELINE_STEPS.map((step) => (
                  <option key={step.key} value={step.key}>
                    {step.label}
                  </option>
                ))}
              </select>
            </div>
            <div style={styles.addFormField}>
              <label style={styles.editLabel}>Category</label>
              <select
                value={newDecision.category}
                onChange={(e) =>
                  setNewDecision((prev) => ({
                    ...prev,
                    category: e.target.value,
                  }))
                }
                style={styles.selectInput}
              >
                {CATEGORY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div style={styles.addFormField}>
            <label style={styles.editLabel}>Title</label>
            <input
              type="text"
              value={newDecision.title}
              onChange={(e) =>
                setNewDecision((prev) => ({ ...prev, title: e.target.value }))
              }
              placeholder="e.g., Authentication Strategy"
              style={styles.textInput}
            />
          </div>
          <div style={styles.addFormField}>
            <label style={styles.editLabel}>Description</label>
            <textarea
              value={newDecision.description}
              onChange={(e) =>
                setNewDecision((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder="What this decision is about..."
              style={styles.editTextarea}
              rows={2}
            />
          </div>
          <div style={styles.addFormField}>
            <label style={styles.editLabel}>Value</label>
            <textarea
              value={newDecision.chosen_value}
              onChange={(e) =>
                setNewDecision((prev) => ({
                  ...prev,
                  chosen_value: e.target.value,
                }))
              }
              placeholder="The decided value (e.g., JWT with refresh tokens)"
              style={styles.editTextarea}
              rows={2}
            />
          </div>
          <div style={styles.addFormField}>
            <label style={styles.editLabel}>Reasoning (optional)</label>
            <textarea
              value={newDecision.reasoning}
              onChange={(e) =>
                setNewDecision((prev) => ({
                  ...prev,
                  reasoning: e.target.value,
                }))
              }
              placeholder="Why this value was chosen..."
              style={styles.editTextarea}
              rows={2}
            />
          </div>
          <div style={styles.editActions}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAddForm(false)}
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleCreateDecision}
              loading={createMutation.isPending}
              disabled={
                !newDecision.title.trim() || !newDecision.chosen_value.trim()
              }
            >
              Create Decision
            </Button>
          </div>
        </div>
      )}

      {/* Empty state */}
      {totalCount === 0 && !showAddForm && (
        <div style={styles.emptyState}>
          <Text style={styles.emptyText}>
            No decisions have been recorded yet. Decisions will appear here as
            the project is processed through the pipeline, or you can add your
            own.
          </Text>
        </div>
      )}

      {/* Grouped decisions */}
      {totalCount > 0 && (
        <div style={styles.decisionsContainer}>
          {orderedSteps.map((step) => {
            const stepDecisions = groupedDecisions[step.key] || [];
            const isCollapsed = collapsedSteps[step.key];
            const stepCount = byStep[step.key] || stepDecisions.length;

            return (
              <div key={step.key} style={styles.stepSection}>
                {/* Step header - collapsible */}
                <button
                  style={styles.stepHeader}
                  onClick={() => toggleStep(step.key)}
                  aria-expanded={!isCollapsed}
                >
                  <div style={styles.stepHeaderLeft}>
                    <span style={styles.chevron}>
                      {isCollapsed ? '\u25B6' : '\u25BC'}
                    </span>
                    <Text style={styles.stepTitle}>{step.label}</Text>
                    <Badge variant="neutral" size="sm">
                      {stepCount}
                    </Badge>
                  </div>
                </button>

                {/* Step decisions */}
                {!isCollapsed && (
                  <div style={styles.decisionsList}>
                    {stepDecisions.map((decision) => (
                      <DecisionCard
                        key={decision.id}
                        decision={decision}
                        isEditing={editingDecisionId === decision.id}
                        editChosenValue={editChosenValue}
                        editReasoning={editReasoning}
                        onEditChosenValueChange={setEditChosenValue}
                        onEditReasoningChange={setEditReasoning}
                        onStartEdit={() => handleStartEdit(decision)}
                        onCancelEdit={handleCancelEdit}
                        onSaveOverride={handleSaveOverride}
                        onReset={() => handleReset(decision.id)}
                        onDelete={() => deleteMutation.mutate(decision.id)}
                        onRestore={() => restoreMutation.mutate(decision.id)}
                        isSaving={previewMutation.isPending}
                        isResetting={resetMutation.isPending}
                        isDeleting={deleteMutation.isPending}
                        isRestoring={restoreMutation.isPending}
                        isReasoningExpanded={!!expandedReasoning[decision.id]}
                        isAlternativesExpanded={
                          !!expandedAlternatives[decision.id]
                        }
                        onToggleReasoning={() => toggleReasoning(decision.id)}
                        onToggleAlternatives={() =>
                          toggleAlternatives(decision.id)
                        }
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Cascade Preview Modal */}
      {previewData && (
        <div style={styles.cascadeOverlay} onClick={handleDismissPreview}>
          <div style={styles.cascadeModal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.cascadeHeader}>
              <Text style={styles.cascadeTitle}>Preview Changes</Text>
              <Button variant="ghost" size="sm" onClick={handleDismissPreview}>
                Close
              </Button>
            </div>

            {/* Decision diff */}
            <div style={styles.cascadeDiffSection}>
              <Text style={styles.cascadeSectionLabel}>Decision Change</Text>
              <div style={styles.cascadeDiffContent}>
                <TextDiff oldText={previewData.old_value} newText={previewData.new_value} />
              </div>
            </div>

            {/* Summary */}
            {previewData.summary && (
              <Text style={styles.cascadeSummary}>{previewData.summary}</Text>
            )}

            {/* Cascade changes */}
            {previewData.cascade_changes?.length > 0 ? (
              <div style={styles.cascadeChanges}>
                <Text style={styles.cascadeSectionLabel}>
                  Affected Artifacts ({previewData.cascade_changes.length})
                </Text>
                {previewData.cascade_changes.map((change, i) => (
                  <label key={i} style={styles.cascadeChangeItem}>
                    <input
                      type="checkbox"
                      checked={!!selectedCascades[i]}
                      onChange={() => toggleCascadeSelection(i)}
                      style={styles.cascadeCheckbox}
                    />
                    <div style={styles.cascadeChangeContent}>
                      <div style={styles.cascadeChangeHeader}>
                        <Badge variant="neutral" size="sm">{change.artifact_type}</Badge>
                        <Text style={styles.cascadeChangeId}>{change.artifact_id}</Text>
                        <Text style={styles.cascadeChangeField}>.{change.field}</Text>
                      </div>
                      <div style={styles.cascadeDiffContent}>
                        <TextDiff oldText={change.old_value} newText={change.new_value} />
                      </div>
                      {change.reason && (
                        <Text style={styles.cascadeChangeReason}>{change.reason}</Text>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            ) : (
              <Text style={styles.cascadeNoChanges}>
                No downstream artifacts need updating.
              </Text>
            )}

            {/* Actions */}
            <div style={styles.cascadeActions}>
              <Button variant="ghost" size="sm" onClick={handleDismissPreview}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleApplyCascade}
                loading={applyMutation.isPending}
              >
                {previewData.cascade_changes?.length > 0
                  ? `Apply Override + ${Object.values(selectedCascades).filter(Boolean).length} Changes`
                  : 'Apply Override'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

// Individual decision card
const DecisionCard = ({
  decision,
  isEditing,
  editChosenValue,
  editReasoning,
  onEditChosenValueChange,
  onEditReasoningChange,
  onStartEdit,
  onCancelEdit,
  onSaveOverride,
  onReset,
  onDelete,
  onRestore,
  isSaving,
  isResetting,
  isDeleting,
  isRestoring,
  isReasoningExpanded,
  isAlternativesExpanded,
  onToggleReasoning,
  onToggleAlternatives,
}) => {
  const categoryConfig = CATEGORY_CONFIG[decision.category] || {
    label: decision.category || 'General',
    color: tokens.colors.neutral[500],
  };

  const hasAlternatives =
    decision.alternatives && decision.alternatives.length > 0;
  const hasReasoning = decision.reasoning && decision.reasoning.trim();
  const confidenceColor = getConfidenceColor(decision.confidence);
  const isPendingDelete = decision.is_pending_delete;

  return (
    <div
      style={{
        ...styles.decisionCard,
        ...(isPendingDelete ? styles.pendingDeleteCard : {}),
      }}
    >
      {/* Card header: title + badges */}
      <div style={styles.decisionHeader}>
        <div style={styles.decisionTitleRow}>
          {/* Confidence dot */}
          {decision.confidence != null && (
            <Tooltip
              content={`Confidence: ${Math.round(decision.confidence * 100)}%`}
              position="top"
            >
              <span
                style={{
                  ...styles.confidenceDot,
                  backgroundColor: confidenceColor,
                }}
              />
            </Tooltip>
          )}
          <Text
            style={{
              ...styles.decisionTitle,
              ...(isPendingDelete ? styles.pendingDeleteText : {}),
            }}
          >
            {decision.title}
          </Text>
          <Badge
            variant="neutral"
            size="sm"
            style={{
              backgroundColor: `${categoryConfig.color}20`,
              color: categoryConfig.color,
              borderColor: `${categoryConfig.color}30`,
            }}
          >
            {categoryConfig.label}
          </Badge>
          {decision.is_user_override && (
            <Badge variant="info" size="sm">
              User Override
            </Badge>
          )}
          {isPendingDelete && (
            <Badge variant="error" size="sm">
              Pending Delete
            </Badge>
          )}
        </div>
        <div style={styles.decisionActions}>
          {!isEditing && !isPendingDelete && (
            <>
              <Button variant="ghost" size="sm" onClick={onStartEdit}>
                Edit
              </Button>
              {decision.is_user_override && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onReset}
                  loading={isResetting}
                >
                  Reset
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={onDelete}
                loading={isDeleting}
                style={styles.deleteButton}
              >
                Delete
              </Button>
            </>
          )}
          {isPendingDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRestore}
              loading={isRestoring}
            >
              Restore
            </Button>
          )}
        </div>
      </div>

      {/* Description */}
      {decision.description && (
        <Text
          style={{
            ...styles.decisionDescription,
            ...(isPendingDelete ? styles.pendingDeleteText : {}),
          }}
        >
          {decision.description}
        </Text>
      )}

      {/* Chosen value or edit form */}
      {isEditing ? (
        <div style={styles.editContainer}>
          <label style={styles.editLabel}>New Value</label>
          <textarea
            value={editChosenValue}
            onChange={(e) => onEditChosenValueChange(e.target.value)}
            placeholder="Enter new value..."
            style={styles.editTextarea}
            rows={2}
            autoFocus
          />
          {/* Live diff preview */}
          {editChosenValue.trim() && editChosenValue.trim() !== decision.chosen_value && (
            <div style={styles.diffPreview}>
              <label style={styles.editLabel}>Preview</label>
              <div style={styles.diffPreviewContent}>
                <TextDiff oldText={decision.chosen_value} newText={editChosenValue.trim()} />
              </div>
            </div>
          )}
          <label style={styles.editLabel}>Reasoning (optional)</label>
          <textarea
            value={editReasoning}
            onChange={(e) => onEditReasoningChange(e.target.value)}
            placeholder="Why are you changing this decision?"
            style={styles.editTextarea}
            rows={2}
          />
          <div style={styles.editActions}>
            <Button
              variant="ghost"
              size="sm"
              onClick={onCancelEdit}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={onSaveOverride}
              loading={isSaving}
              disabled={!editChosenValue.trim()}
            >
              {isSaving ? 'Analyzing...' : 'Preview Changes'}
            </Button>
          </div>
        </div>
      ) : (
        <div
          style={{
            ...styles.chosenValueContainer,
            ...(isPendingDelete ? styles.pendingDeleteValueContainer : {}),
          }}
        >
          <Text
            style={{
              ...styles.chosenValue,
              ...(isPendingDelete ? styles.pendingDeleteText : {}),
            }}
          >
            {decision.chosen_value}
          </Text>
          {decision.is_user_override && decision.original_value && (
            <div style={styles.overrideDiffContainer}>
              <TextDiff oldText={decision.original_value} newText={decision.chosen_value} />
            </div>
          )}
        </div>
      )}

      {/* Reasoning (collapsible) */}
      {hasReasoning && !isEditing && !isPendingDelete && (
        <div style={styles.expandableSection}>
          <button style={styles.expandableToggle} onClick={onToggleReasoning}>
            <span style={styles.expandableChevron}>
              {isReasoningExpanded ? '\u25BC' : '\u25B6'}
            </span>
            <Text style={styles.expandableLabel}>Reasoning</Text>
          </button>
          {isReasoningExpanded && (
            <div style={styles.expandableContent}>
              <Text style={styles.reasoningText}>{decision.reasoning}</Text>
            </div>
          )}
        </div>
      )}

      {/* Alternatives (collapsible) */}
      {hasAlternatives && !isEditing && !isPendingDelete && (
        <div style={styles.expandableSection}>
          <button
            style={styles.expandableToggle}
            onClick={onToggleAlternatives}
          >
            <span style={styles.expandableChevron}>
              {isAlternativesExpanded ? '\u25BC' : '\u25B6'}
            </span>
            <Text style={styles.expandableLabel}>
              Other options ({decision.alternatives.length})
            </Text>
          </button>
          {isAlternativesExpanded && (
            <div style={styles.alternativesList}>
              {decision.alternatives.map((alt, index) => (
                <div key={index} style={styles.alternativeItem}>
                  <Text style={styles.alternativeValue}>{alt.value}</Text>
                  {alt.pros && (
                    <Text style={styles.alternativePros}>
                      Pros: {alt.pros}
                    </Text>
                  )}
                  {alt.cons && (
                    <Text style={styles.alternativeCons}>
                      Cons: {alt.cons}
                    </Text>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const styles = {
  tabCard: {
    padding: tokens.spacing[6],
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacing[8],
    gap: tokens.spacing[4],
  },
  loadingText: {
    color: 'var(--color-text-muted)',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacing[8],
    gap: tokens.spacing[4],
    textAlign: 'center',
  },
  emptyText: {
    color: 'var(--color-text-muted)',
    maxWidth: '460px',
    lineHeight: 1.6,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: tokens.spacing[6],
    gap: tokens.spacing[4],
    flexWrap: 'wrap',
  },
  headerRight: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: tokens.spacing[2],
  },
  tabTitle: {
    fontSize: tokens.typography.fontSize.xl[0],
    fontWeight: tokens.typography.fontWeight.semibold,
    marginBottom: tokens.spacing[2],
  },
  description: {
    color: 'var(--color-text-muted)',
    fontSize: tokens.typography.fontSize.sm[0],
  },
  headerStats: {
    display: 'flex',
    gap: tokens.spacing[2],
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  // Add Decision form
  addForm: {
    padding: tokens.spacing[4],
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: tokens.borderRadius.lg,
    marginBottom: tokens.spacing[4],
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing[3],
  },
  addFormTitle: {
    fontWeight: tokens.typography.fontWeight.semibold,
    fontSize: tokens.typography.fontSize.base[0],
    marginBottom: tokens.spacing[1],
  },
  addFormGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacing[3],
  },
  addFormField: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing[1],
  },
  textInput: {
    padding: tokens.spacing[3],
    backgroundColor: 'var(--color-bg)',
    border: '1px solid var(--color-border)',
    borderRadius: tokens.borderRadius.md,
    fontSize: tokens.typography.fontSize.sm[0],
    color: 'var(--color-text)',
    fontFamily: 'inherit',
    width: '100%',
    boxSizing: 'border-box',
  },
  selectInput: {
    padding: tokens.spacing[3],
    backgroundColor: 'var(--color-bg)',
    border: '1px solid var(--color-border)',
    borderRadius: tokens.borderRadius.md,
    fontSize: tokens.typography.fontSize.sm[0],
    color: 'var(--color-text)',
    fontFamily: 'inherit',
    width: '100%',
    boxSizing: 'border-box',
    appearance: 'auto',
  },
  decisionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing[4],
  },
  stepSection: {
    marginBottom: tokens.spacing[2],
  },
  stepHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: `${tokens.spacing[3]} ${tokens.spacing[4]}`,
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: tokens.borderRadius.lg,
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
    appearance: 'none',
    textAlign: 'left',
    color: 'var(--color-text)',
    fontFamily: 'inherit',
    fontSize: 'inherit',
  },
  stepHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacing[3],
  },
  chevron: {
    fontSize: '10px',
    color: 'var(--color-text-muted)',
    width: '14px',
    display: 'inline-block',
    textAlign: 'center',
  },
  stepTitle: {
    fontWeight: tokens.typography.fontWeight.semibold,
    fontSize: tokens.typography.fontSize.base[0],
  },
  decisionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing[3],
    marginTop: tokens.spacing[3],
    paddingLeft: tokens.spacing[4],
  },
  decisionCard: {
    padding: tokens.spacing[4],
    backgroundColor: 'var(--color-surface)',
    borderRadius: tokens.borderRadius.lg,
    border: '1px solid var(--color-border)',
  },
  pendingDeleteCard: {
    opacity: 0.65,
    borderColor: `${tokens.colors.error[500]}40`,
    borderStyle: 'dashed',
  },
  pendingDeleteText: {
    textDecoration: 'line-through',
  },
  pendingDeleteValueContainer: {
    opacity: 0.5,
  },
  decisionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: tokens.spacing[2],
    gap: tokens.spacing[3],
  },
  decisionTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacing[2],
    flexWrap: 'wrap',
    flex: 1,
    minWidth: 0,
  },
  confidenceDot: {
    display: 'inline-block',
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  decisionTitle: {
    fontWeight: tokens.typography.fontWeight.medium,
    fontSize: tokens.typography.fontSize.sm[0],
    color: 'var(--color-text)',
  },
  decisionDescription: {
    fontSize: tokens.typography.fontSize.xs[0],
    color: 'var(--color-text-muted)',
    marginBottom: tokens.spacing[3],
    lineHeight: 1.5,
  },
  decisionActions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacing[1],
    flexShrink: 0,
  },
  deleteButton: {
    color: tokens.colors.error[500],
  },
  chosenValueContainer: {
    padding: tokens.spacing[3],
    backgroundColor: 'var(--color-bg)',
    borderRadius: tokens.borderRadius.md,
    marginBottom: tokens.spacing[2],
  },
  chosenValue: {
    fontSize: tokens.typography.fontSize.sm[0],
    color: 'var(--color-text)',
    lineHeight: 1.5,
  },
  originalValue: {
    fontSize: tokens.typography.fontSize.xs[0],
    color: 'var(--color-text-muted)',
    marginTop: tokens.spacing[2],
    fontStyle: 'italic',
  },
  overrideDiffContainer: {
    marginTop: tokens.spacing[2],
    padding: tokens.spacing[2],
    backgroundColor: 'var(--color-bg)',
    borderRadius: tokens.borderRadius.md,
    border: '1px solid var(--color-border)',
  },
  diffPreview: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing[1],
  },
  diffPreviewContent: {
    padding: tokens.spacing[3],
    backgroundColor: 'var(--color-bg)',
    borderRadius: tokens.borderRadius.md,
    border: '1px solid var(--color-border)',
  },
  editContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing[2],
    marginBottom: tokens.spacing[2],
  },
  editLabel: {
    fontSize: tokens.typography.fontSize.xs[0],
    fontWeight: tokens.typography.fontWeight.medium,
    color: 'var(--color-text-muted)',
  },
  editTextarea: {
    padding: tokens.spacing[3],
    backgroundColor: 'var(--color-bg)',
    border: '1px solid var(--color-border)',
    borderRadius: tokens.borderRadius.md,
    fontSize: tokens.typography.fontSize.sm[0],
    color: 'var(--color-text)',
    resize: 'vertical',
    fontFamily: 'inherit',
    width: '100%',
    boxSizing: 'border-box',
  },
  editActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacing[2],
    marginTop: tokens.spacing[1],
  },
  expandableSection: {
    marginTop: tokens.spacing[2],
  },
  expandableToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacing[2],
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: `${tokens.spacing[1]} 0`,
    color: 'var(--color-text-muted)',
    fontFamily: 'inherit',
    fontSize: 'inherit',
  },
  expandableChevron: {
    fontSize: '8px',
    width: '12px',
    display: 'inline-block',
    textAlign: 'center',
  },
  expandableLabel: {
    fontSize: tokens.typography.fontSize.xs[0],
    color: 'var(--color-text-muted)',
    fontWeight: tokens.typography.fontWeight.medium,
  },
  expandableContent: {
    padding: tokens.spacing[3],
    backgroundColor: 'var(--color-bg)',
    borderRadius: tokens.borderRadius.md,
    marginTop: tokens.spacing[2],
  },
  reasoningText: {
    fontSize: tokens.typography.fontSize.sm[0],
    color: 'var(--color-text-muted)',
    lineHeight: 1.6,
  },
  alternativesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing[2],
    marginTop: tokens.spacing[2],
  },
  alternativeItem: {
    padding: tokens.spacing[3],
    backgroundColor: 'var(--color-bg)',
    borderRadius: tokens.borderRadius.md,
    border: '1px solid var(--color-border)',
  },
  alternativeValue: {
    fontSize: tokens.typography.fontSize.sm[0],
    fontWeight: tokens.typography.fontWeight.medium,
    color: 'var(--color-text)',
    marginBottom: tokens.spacing[1],
  },
  alternativePros: {
    fontSize: tokens.typography.fontSize.xs[0],
    color: tokens.colors.success[400],
    lineHeight: 1.5,
  },
  alternativeCons: {
    fontSize: tokens.typography.fontSize.xs[0],
    color: tokens.colors.error[400],
    lineHeight: 1.5,
    marginTop: tokens.spacing[1],
  },
  // Cascade preview modal
  cascadeOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: tokens.spacing[4],
  },
  cascadeModal: {
    backgroundColor: 'var(--color-surface)',
    borderRadius: tokens.borderRadius.xl,
    border: '1px solid var(--color-border)',
    maxWidth: '720px',
    width: '100%',
    maxHeight: '80vh',
    overflow: 'auto',
    padding: tokens.spacing[6],
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing[4],
  },
  cascadeHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cascadeTitle: {
    fontSize: tokens.typography.fontSize.lg[0],
    fontWeight: tokens.typography.fontWeight.semibold,
  },
  cascadeDiffSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing[2],
  },
  cascadeSectionLabel: {
    fontSize: tokens.typography.fontSize.xs[0],
    fontWeight: tokens.typography.fontWeight.semibold,
    color: 'var(--color-text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  cascadeDiffContent: {
    padding: tokens.spacing[3],
    backgroundColor: 'var(--color-bg)',
    borderRadius: tokens.borderRadius.md,
    border: '1px solid var(--color-border)',
  },
  cascadeSummary: {
    fontSize: tokens.typography.fontSize.sm[0],
    color: 'var(--color-text-muted)',
    lineHeight: 1.5,
  },
  cascadeChanges: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing[3],
  },
  cascadeChangeItem: {
    display: 'flex',
    gap: tokens.spacing[3],
    padding: tokens.spacing[3],
    backgroundColor: 'var(--color-bg)',
    borderRadius: tokens.borderRadius.md,
    border: '1px solid var(--color-border)',
    cursor: 'pointer',
    alignItems: 'flex-start',
  },
  cascadeCheckbox: {
    marginTop: '3px',
    flexShrink: 0,
  },
  cascadeChangeContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing[2],
    minWidth: 0,
  },
  cascadeChangeHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacing[2],
    flexWrap: 'wrap',
  },
  cascadeChangeId: {
    fontSize: tokens.typography.fontSize.sm[0],
    fontWeight: tokens.typography.fontWeight.medium,
    color: 'var(--color-text)',
  },
  cascadeChangeField: {
    fontSize: tokens.typography.fontSize.xs[0],
    color: 'var(--color-text-muted)',
    fontFamily: tokens.typography.fontFamily.mono.join(', '),
  },
  cascadeChangeReason: {
    fontSize: tokens.typography.fontSize.xs[0],
    color: 'var(--color-text-muted)',
    fontStyle: 'italic',
  },
  cascadeNoChanges: {
    fontSize: tokens.typography.fontSize.sm[0],
    color: 'var(--color-text-muted)',
    textAlign: 'center',
    padding: tokens.spacing[4],
  },
  cascadeActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacing[2],
    borderTop: '1px solid var(--color-border)',
    paddingTop: tokens.spacing[4],
  },
};

export default DecisionsTab;
